import type { Client } from 'braintree-web';

import { createAction } from 'Lib/reduxUtils';

import type { PaymentType } from 'Store/entities/products.types';

import {
  PURCHASE_3D,
  PURCHASE_3D_ERROR_RESPONSE,
  PURCHASE_ADD_CART,
  PURCHASE_ATTEMPT,
  PURCHASE_CLEAR_ERROR_RESPONSE,
  PURCHASE_CLEAR_TAX_DETAILS,
  PURCHASE_DELETE_BRAINTREE_PAYMENT_METHOD,
  PURCHASE_DELETE_SAVED_CARD,
  PURCHASE_DISABLE_AUTORENEW,
  PURCHASE_DISABLE_AUTORENEW_RESPONSE,
  PURCHASE_ENABLE_AUTORENEW,
  PURCHASE_ENABLE_AUTORENEW_RESPONSE,
  PURCHASE_ERROR_RESPONSE,
  PURCHASE_FORM_VALIDATION,
  PURCHASE_GET_LATEST_CARD_INFO_RESPONSE,
  PURCHASE_GET_PAYMENT_METHODS,
  PURCHASE_GET_PAYMENT_METHODS_CLEAR_LOADING,
  PURCHASE_GET_PAYMENT_METHODS_ERROR_RESPONSE,
  PURCHASE_GET_PAYMENT_METHODS_RESPONSE,
  PURCHASE_GET_TAX,
  PURCHASE_GET_TAX_DETAILS_RESPONSE,
  PURCHASE_POST,
  PURCHASE_REMOVE_PAYWALL_SOURCE,
  PURCHASE_RESTORE_ACCOUNT,
  PURCHASE_RESTORE_ACCOUNT_CLEAR_ERROR_RESPONSE,
  PURCHASE_RESTORE_ACCOUNT_ERROR_RESPONSE,
  PURCHASE_RESTORE_ACCOUNT_RESPONSE,
  PURCHASE_SAVE_BRAINTREE_PAYMENT_METHOD,
  PURCHASE_SET_PAYMENT_SDK_LOADING,
  PURCHASE_SUCCESSFUL_POST,
  PURCHASE_UPDATE_EMAIL,
  PURCHASE_UPDATE_EMAIL_CLEAR_ERROR_RESPONSE,
  PURCHASE_UPDATE_EMAIL_ERROR_RESPONSE,
  PURCHASE_UPDATE_EMAIL_SUCCESS,
  PURCHASE_UPDATE_FORM,
  PURCHASE_UPDATE_LATEST_CARD,
  PURCHASE_UPDATE_LATEST_CARD_CLEAR_ERR_RESPONSE,
  PURCHASE_UPDATE_LATEST_CARD_CLEAR_SUCCESS,
  PURCHASE_UPDATE_LATEST_CARD_ERR_RESPONSE,
  PURCHASE_UPDATE_LATEST_CARD_RESPONSE,
  SET_3DS_TYPE,
  SET_ADYEN_DEVICE_FINGERPRINT,
  SET_BRAINTREE_CLIENT_TOKEN,
  SET_BRAINTREE_DEVICE_FINGERPRINT,
  SET_BRAINTREE_ERROR,
  SET_CC_TYPE,
  SET_CHECKOUT_PAGE_TYPE,
  SET_CHECKOUT_PAGE_VERSION,
  SET_PAYMENT_METHOD,
  SET_SAVE_CARD_BOX,
  SET_UPSELL_SHOWN,
  USE_BOUNCER_BYPASS,
} from './constants';
import { parseError } from './logic';
import type {
  Card,
  CheckoutItem,
  PaymentMethodType,
  PurchaseFormData,
  Purchases,
  SavedPaymentMethodPayload,
  Tax,
  TaxData,
} from './types';

export const purchasePost = createAction(
  PURCHASE_POST,
  (payload: { postData: any; productType: string; paymentType?: PaymentType }) => payload
);
export const purchaseAttempt = createAction(PURCHASE_ATTEMPT);
export const purchaseResponse = createAction(
  PURCHASE_SUCCESSFUL_POST,
  (payload: Purchases) => payload
);
export const purchaseErrorResponse = createAction(
  PURCHASE_ERROR_RESPONSE,
  (error: Partial<ReturnType<typeof parseError>>) => error
);
export const purchase3D = createAction(
  PURCHASE_3D,
  (payload: { id: string; md?: string; paResponse?: string }) => payload
);
export const purchase3DErrorResponse = createAction(
  PURCHASE_3D_ERROR_RESPONSE,
  (error: Partial<ReturnType<typeof parseError>>) => error
);
export const purchaseClearErrorResponse = createAction(PURCHASE_CLEAR_ERROR_RESPONSE);

export const getLatestCardInfoResponse = createAction(
  PURCHASE_GET_LATEST_CARD_INFO_RESPONSE,
  (payload: { card: Card }) => payload
);
export const getPaymentMethodsInfo = createAction(PURCHASE_GET_PAYMENT_METHODS);
export const getPaymentMethodsResponse = createAction(
  PURCHASE_GET_PAYMENT_METHODS_RESPONSE,
  (payload: any) => payload
);
export const getPaymentMethodsErrorResponse = createAction(
  PURCHASE_GET_PAYMENT_METHODS_ERROR_RESPONSE
);
export const getPaymentMethodsInfoClearLoading = createAction(
  PURCHASE_GET_PAYMENT_METHODS_CLEAR_LOADING
);

export const setBraintreeClientTokenResponse = createAction(
  SET_BRAINTREE_CLIENT_TOKEN,
  (payload: Client) => payload
);
export const setBraintreeError = createAction(SET_BRAINTREE_ERROR, (hasError: boolean = true) => ({
  hasError,
}));

export const purchaseAddToCart = createAction(
  PURCHASE_ADD_CART,
  ({
    actionContext,
    amount,
    basePrice,
    destinationRoute = { route: 'checkout', params: {} },
    discountCampaign,
    discountTestGroup,
    featuresList,
    from,
    isSuperBoostVisible,
    paywallVersion,
    productId,
    products,
    purchasedFromIncognito,
    shouldAutoRewind,
    shouldAutoStartBoost,
    shouldAutoStartCompoundBoost,
    sourceSessionEvent,
    templateUuids,
    testName,
    upsell,
  }: CheckoutItem) => ({
    actionContext,
    amount,
    basePrice,
    destinationRoute,
    discountCampaign,
    discountTestGroup,
    featuresList,
    from,
    isSuperBoostVisible,
    paywallVersion,
    productId,
    products,
    purchasedFromIncognito,
    shouldAutoRewind,
    shouldAutoStartBoost,
    shouldAutoStartCompoundBoost,
    sourceSessionEvent,
    templateUuids,
    testName,
    upsell,
  })
);

export const purchaseRemovePaywallSource = createAction(PURCHASE_REMOVE_PAYWALL_SOURCE);

export const purchaseEnableAutoRenew = createAction(
  PURCHASE_ENABLE_AUTORENEW,
  (payload: string) => payload
);

export const purchaseEnableAutoRenewResponse = createAction(
  PURCHASE_ENABLE_AUTORENEW_RESPONSE,
  (payload: { product_type: string }) => payload
);

export const purchaseDisableAutoRenew = createAction(
  PURCHASE_DISABLE_AUTORENEW,
  (purchaseId: string, isFromCancellationSource?: boolean, isMembership?: boolean) => ({
    purchaseId,
    isFromCancellationSource,
    isMembership,
  })
);

export const purchaseDisableAutoRenewResponse = createAction(
  PURCHASE_DISABLE_AUTORENEW_RESPONSE,
  (payload: { product_type: string }) => payload
);

export const purchasePostUpdateCard = createAction(
  PURCHASE_UPDATE_LATEST_CARD,
  // TODO: REMOVE-TYPE-ASSERTION - "as Card" is inaccurate since Card has non-optional properties.
  (cardInfo: Card = {} as Card, encryptedCardData: any) => ({ cardInfo, encryptedCardData })
);

export const purchasePostUpdateCardResponse = createAction(
  PURCHASE_UPDATE_LATEST_CARD_RESPONSE,
  (payload: any) => payload
);

export const purchaseSaveBraintreePaymentMethod = createAction(
  PURCHASE_SAVE_BRAINTREE_PAYMENT_METHOD,
  (paymentMethod: PaymentMethodType, data: SavedPaymentMethodPayload) => ({ paymentMethod, data })
);

export const purchaseDeleteBraintreePaymentMethod = createAction(
  PURCHASE_DELETE_BRAINTREE_PAYMENT_METHOD,
  (paymentMethod: PaymentMethodType) => paymentMethod
);

export const setPaymentSdkIsLoading = createAction(
  PURCHASE_SET_PAYMENT_SDK_LOADING,
  (paymentType: PaymentMethodType, isLoading: boolean) => ({ paymentType, isLoading })
);

export const purchasePostUpdateCardErrorResponse = createAction(
  PURCHASE_UPDATE_LATEST_CARD_ERR_RESPONSE,
  (error: Partial<ReturnType<typeof parseError>>) => error
);

export const purchasePostUpdateCardClearError = createAction(
  PURCHASE_UPDATE_LATEST_CARD_CLEAR_ERR_RESPONSE
);
export const purchaseClearUpdateCardSuccess = createAction(
  PURCHASE_UPDATE_LATEST_CARD_CLEAR_SUCCESS
);

export const purchaseDeleteSavedCard = createAction(PURCHASE_DELETE_SAVED_CARD);

export const purchaseGetTax = createAction(PURCHASE_GET_TAX, (zip: string, taxData: Tax) => ({
  zip,
  taxData,
}));

export const purchaseGetTaxDetailsResponse = createAction(
  PURCHASE_GET_TAX_DETAILS_RESPONSE,
  (taxData: TaxData) => taxData
);

export const purchaseClearTaxDetails = createAction(PURCHASE_CLEAR_TAX_DETAILS);

export const purchaseRestoreAccount = createAction(
  PURCHASE_RESTORE_ACCOUNT,
  (payload: string) => payload
);
export const purchaseRestoreAccountResponse = createAction(
  PURCHASE_RESTORE_ACCOUNT_RESPONSE,
  (payload: Purchases) => payload
);
export const purchaseRestoreAccountError = createAction(PURCHASE_RESTORE_ACCOUNT_ERROR_RESPONSE);
export const purchaseRestoreAccountClearError = createAction(
  PURCHASE_RESTORE_ACCOUNT_CLEAR_ERROR_RESPONSE
);

export const updateEmail = createAction(
  PURCHASE_UPDATE_EMAIL,
  (purchaseId: string, email: string) => ({
    purchaseId,
    email,
  })
);
export const updateEmailSuccess = createAction(PURCHASE_UPDATE_EMAIL_SUCCESS, (email: string) => ({
  email,
}));
export const updateEmailError = createAction(PURCHASE_UPDATE_EMAIL_ERROR_RESPONSE);
export const updateEmailClearError = createAction(PURCHASE_UPDATE_EMAIL_CLEAR_ERROR_RESPONSE);
export const setCheckoutPageType = createAction(
  SET_CHECKOUT_PAGE_TYPE,
  (payload: string) => payload
);
export const setCheckoutPageVersion = createAction(
  SET_CHECKOUT_PAGE_VERSION,
  (payload: string) => payload
);
export const validatePurchaseForm = createAction(
  PURCHASE_FORM_VALIDATION,
  (isFormValidated: boolean) => ({ isFormValidated })
);
export const updatePurchaseForm = createAction(
  PURCHASE_UPDATE_FORM,
  (formData: PurchaseFormData) => ({
    formData: { ...formData },
  })
);

// Set payment method for Analytics
export const setPaymentMethod = createAction(SET_PAYMENT_METHOD, (payload: string) => payload);

export const setCCType = createAction(SET_CC_TYPE, (payload: string) => payload);

export const set3DSType = createAction(SET_3DS_TYPE, (payload: number) => payload);

export const setUpsellShown = createAction(
  SET_UPSELL_SHOWN,
  (payload: string | { upsellType?: string; upsellTs?: number; stopShowingUpsell?: boolean }) =>
    payload
);

export const setSaveCardBitwise = createAction(
  SET_SAVE_CARD_BOX,
  (payload: number | null) => payload
);

export const useBouncerBypass = createAction(USE_BOUNCER_BYPASS);

export const setAdyenDeviceFingerprint = createAction(
  SET_ADYEN_DEVICE_FINGERPRINT,
  (fingerprint: string) => ({
    fingerprint,
  })
);

export const setBraintreeDeviceFingerprint = createAction(
  SET_BRAINTREE_DEVICE_FINGERPRINT,
  (fingerprint: string) => ({
    fingerprint,
  })
);
